export type ExcelImportStatus = "idle" | "in_progress" | "failed" | "completed";

export type ExcelImportProgress = {
  status: ExcelImportStatus;
  completedPhases: string[];
  lastFailedPhase?: string;
  lastError?: string;
  startedAt?: string;
  updatedAt?: string;
  completedAt?: string;
  configHash?: string;
};

export const emptyImportProgress = (): ExcelImportProgress => ({
  status: "idle",
  completedPhases: [],
});

const sortValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortValue);
  }
  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = sortValue((value as Record<string, unknown>)[key]);
          return acc;
        },
        {} as Record<string, unknown>,
      );
  }
  return value;
};

/** Stable hash so resume is invalidated when onboarding payload changes. */
export const hashImportConfig = (payload: Record<string, unknown>): string => {
  const stable = JSON.stringify(sortValue(payload));
  let hash = 0;
  for (let index = 0; index < stable.length; index++) {
    hash = (hash << 5) - hash + stable.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

export const parseImportProgress = (
  json: unknown,
): ExcelImportProgress | null => {
  if (!json || typeof json !== "object") return null;
  const record = json as Record<string, unknown>;

  const statusRaw = String(record.status ?? "idle")
    .trim()
    .toLowerCase();
  const status: ExcelImportStatus =
    statusRaw === "in_progress" ||
    statusRaw === "failed" ||
    statusRaw === "completed"
      ? statusRaw
      : "idle";

  const completedPhases = Array.isArray(record.completedPhases)
    ? record.completedPhases.filter(
        (phase): phase is string =>
          typeof phase === "string" && phase.trim() !== "",
      )
    : [];

  return {
    status,
    completedPhases,
    ...(typeof record.lastFailedPhase === "string" && record.lastFailedPhase
      ? { lastFailedPhase: record.lastFailedPhase }
      : {}),
    ...(typeof record.lastError === "string" && record.lastError
      ? { lastError: record.lastError }
      : {}),
    ...(typeof record.startedAt === "string"
      ? { startedAt: record.startedAt }
      : {}),
    ...(typeof record.updatedAt === "string"
      ? { updatedAt: record.updatedAt }
      : {}),
    ...(typeof record.completedAt === "string"
      ? { completedAt: record.completedAt }
      : {}),
    ...(typeof record.configHash === "string"
      ? { configHash: record.configHash }
      : {}),
  };
};

export type ImportRunMode = "fresh" | "resume";

/** Resume only when the same payload failed or was interrupted mid-import. */
export const resolveImportRunMode = (
  stored: ExcelImportProgress | null,
  configHash: string,
): { mode: ImportRunMode; completedPhases: string[] } => {
  if (
    stored &&
    stored.configHash === configHash &&
    (stored.status === "failed" || stored.status === "in_progress") &&
    stored.completedPhases.length > 0
  ) {
    return { mode: "resume", completedPhases: [...stored.completedPhases] };
  }
  return { mode: "fresh", completedPhases: [] };
};

export const buildImportProgressPayload = (
  tenantId: number,
  propertyId: number,
  productId: number,
  progress: ExcelImportProgress,
) => ({
  tenantId,
  propertyId,
  productId,
  ...progress,
});
